<template>
  <Dialog :open="!!message" @close="emits('close')">
    <DialogContainer>
      <DialogHeader :title="$t('inbox.message')" icon="chevron-left" @close="emits('close')">
        <Dropdown v-if="isWidget">
          <Button
            icon-only
            icon="menu"
            size="large"
            type="secondary"
            class="mr-0.5"
            @click.stop.prevent
          />
          <template #dropdown>
            <DropdownMenuItem icon="bell" icon-color="text-accent-500" @click="emits('notify')">
              {{ $t('messaging.compose-message.notify-again') }}
            </DropdownMenuItem>
            <DropdownMenuItem
              icon="pencil"
              :disabled="!isEditAvailable"
              @click="emits('edit')"
              icon-color="text-accent-500"
            >
              <template v-if="isEditAvailable">
                <span>{{ $t('messaging.compose-message.edit') }}</span>
                &nbsp;
                <span class="text-gray-600">
                  {{ $t('messaging.compose-message.min-left', { min: 60 - minutesDifference }) }}
                </span>
              </template>
              <template v-else> {{ $t('messaging.compose-message.unable-to-edit') }} </template>
            </DropdownMenuItem>
            <Separator />
            <DropdownMenuItem
              icon="trash"
              icon-color="text-accent-500"
              :disabled="!isDeleteAvailable"
              @click="emits('delete')"
            >
              {{
                isDeleteAvailable
                  ? $t('messaging.compose-message.delete')
                  : message?.is_read
                  ? $t('messaging.compose-message.unable-to-delete-read')
                  : $t('messaging.compose-message.unable-to-delete')
              }}
            </DropdownMenuItem>
          </template>
        </Dropdown>
      </DialogHeader>
      <div class="message-details">
        <div class="message-details__title">{{ message?.title }}</div>
        <div class="flex gap-2 pb-2" v-if="isWidget">
          <Status v-if="message?.is_urgent" color="danger">
            <span>{{ $t('inbox.urgent') }}</span>
          </Status>
          <span v-if="message?.is_read" class="is-read text-gray-500">
            <SvgIcon name="double-checkmark" :size="16" class="shrink-0" />
            <span>{{ $t('messaging.read') }}</span>
          </span>
          <span v-if="message?.is_updated" class="is-read text-gray-500">
            <SvgIcon name="pencil" :size="16" class="shrink-0" />
            <span>{{ $t('messaging.edited') }}</span>
          </span>
        </div>
        <div class="message-details__item">
          <SvgIcon :size="20" name="person" class="mt-0.5 shrink-0" />
          <span class="message-details__item-label">{{ message?.practitioner_name }}</span>
        </div>
        <div class="message-details__item">
          <SvgIcon :size="20" name="clock" class="mt-0.5 shrink-0" />
          <span class="message-details__item-label">{{
            dayjs(message?.created_at).format('MMMM DD, HH:mm')
          }}</span>
        </div>
        <div class="message-details__item">
          <SvgIcon :size="20" name="text" class="mt-0.5 shrink-0" />
          <span class="message-details__item-label">{{ message?.body }}</span>
        </div>
      </div>
    </DialogContainer>
  </Dialog>
</template>
<script setup lang="ts">
import { computed } from 'vue'

import Button from '@/components/common/buttons/Button.vue'
import Dialog from '@/components/common/dialog/Dialog.vue'
import DialogContainer from '@/components/common/dialog/DialogContainer.vue'
import DialogHeader from '@/components/common/dialog/DialogHeader.vue'
import Dropdown from '@/components/common/dropdown/Dropdown.vue'
import DropdownMenuItem from '@/components/common/dropdown/DropdownMenuItem.vue'
import Separator from '@/components/common/dropdown/Separator.vue'
import Status from '@/components/common/status/Status.vue'
import SvgIcon from '@/components/common/SvgIcon.vue'
import dayjs from '@/dayjs'
import type { Message, PatientMessage } from '@/generated/api'

const props = defineProps<{
  message: PatientMessage | null | Message
  isWidget?: boolean
}>()

const minutesDifference = computed(() => {
  return dayjs().diff(dayjs(props.message?.created_at), 'minute')
})

const isEditAvailable = computed(() => {
  return minutesDifference.value < 60
})

const isDeleteAvailable = computed(() => {
  return !props.message?.is_read && minutesDifference.value < 90
})

const emits = defineEmits<{
  close: []
  edit: []
  delete: []
  notify: []
}>()
</script>

<style scoped lang="postcss">
.message-details {
  @apply p-5 flex flex-col gap-5;

  .is-read {
    @apply flex flex-row items-center gap-1 mr-4 text-base;
  }
  &__title {
    @apply text-accent-500 font-bold text-2xl;
  }
  &__item {
    @apply flex gap-5 pt-5 pl-3;
    &-label {
      @apply border-b border-gray-200 grow pb-5;
    }
  }
}
</style>
